import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Search, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function PurchasePayments() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [vendors, setVendors] = useState([]);
  const [rowsError, setRowsError] = useState("");
  const [vendorsError, setVendorsError] = useState("");
  const [billsError, setBillsError] = useState("");
  const [bills, setBills] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vendor_id: "", purchase_id: "", amount: 0, payment_mode: "Bank", reference: "", notes: "" });
  const billsRequestRef = useRef(0);
  const location = useLocation();

  const load = useCallback(async () => {
    try { const r = await http.get("/purchase-payments", { params: { search } }); setRows(r.data); setRowsError(""); }
    catch (e) { setRowsError(requestErrorMessage(e, "load purchase payments")); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const loadVendors = useCallback(async () => {
    try { const r = await http.get("/vendors"); setVendors(r.data); setVendorsError(""); }
    catch (e) { setVendorsError(requestErrorMessage(e, "load vendors")); }
  }, []);
  useEffect(() => { loadVendors(); }, [loadVendors]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const vid = p.get("vendor"); const pid = p.get("purchase");
    if (vid || pid) { setForm((f) => ({ ...f, vendor_id: vid || "", purchase_id: pid || "" })); setOpen(true); }
  }, [location.search]);

  const loadBills = useCallback(async () => {
    if (!form.vendor_id) return;
    const requestId = ++billsRequestRef.current;
    setBills([]);
    try {
      const r = await http.get("/purchases/purchase_invoice");
      if (requestId !== billsRequestRef.current) return;
      const pending = r.data.filter((b) => b.vendor_id === form.vendor_id && b.balance_due > 0);
      setBills(pending);
      setBillsError("");
    } catch (e) {
      if (requestId === billsRequestRef.current) setBillsError(requestErrorMessage(e, "load vendor bills"));
    }
  }, [form.vendor_id]);

  useEffect(() => {
    if (form.vendor_id) {
      setBillsError("");
      loadBills();
    } else {
      billsRequestRef.current += 1;
      setBills([]);
      setBillsError("");
    }
  }, [form.vendor_id, loadBills]);

  const openNew = () => { setForm({ vendor_id: "", purchase_id: "", amount: 0, payment_mode: "Bank", reference: "", notes: "" }); setOpen(true); };
  const pickBill = (id) => { const b = bills.find((x) => x.id === id); setForm({ ...form, purchase_id: id, amount: b ? b.balance_due : form.amount }); };
  const save = async () => {
    if (!form.vendor_id) return toast.error("Select vendor");
    if (!form.amount || form.amount <= 0) return toast.error("Enter amount");
    try { await http.post("/purchase-payments", { ...form, purchase_id: form.purchase_id || null }); toast.success("Payment recorded"); setOpen(false); load(); }
    catch (e) { toast.error(requestErrorMessage(e, "record purchase payment")); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Purchase Payments</h1><p className="text-sm text-muted-foreground">{rows.length} vendor payments</p></div>
        <Button onClick={openNew} data-testid="record-purchase-payment-button"><Plus className="h-4 w-4 mr-1" /> Record Payment</Button>
      </div>
      {rowsError && <div role="alert" className="flex items-center justify-between gap-3 border border-rose-200 bg-rose-50 rounded p-3 text-sm text-rose-700"><span>{rowsError}</span><Button size="sm" variant="outline" onClick={load}>Retry payments</Button></div>}
      {vendorsError && <div role="status" className="flex items-center justify-between gap-3 border border-amber-200 bg-amber-50 rounded p-3 text-sm text-amber-800"><span>{vendorsError}; vendor selection is unavailable.</span><Button size="sm" variant="outline" onClick={loadVendors}>Retry vendors</Button></div>}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b"><div className="relative max-w-sm"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="purchase-payment-search" /></div></div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="px-4 py-3 font-medium">Payment No</th><th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Vendor</th><th className="px-4 py-3 font-medium">Bill</th><th className="px-4 py-3 font-medium">Mode</th><th className="px-4 py-3 font-medium text-right">Amount</th></tr></thead>
          <tbody>{rows.map((p) => (<tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`ppay-row-${p.id}`}><td className="px-4 py-3 font-mono font-medium">{p.payment_no}</td><td className="px-4 py-3 text-muted-foreground">{p.payment_date}</td><td className="px-4 py-3">{p.vendor_name}</td><td className="px-4 py-3 font-mono text-xs">{p.purchase_no || "—"}</td><td className="px-4 py-3"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{p.payment_mode}</span></td><td className="px-4 py-3 text-right font-mono text-rose-600 font-semibold">{inr(p.amount)}</td></tr>))}
          {!rowsError && rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground"><Wallet className="h-8 w-8 mx-auto mb-2 opacity-40" />No payments</td></tr>}</tbody>
        </table></div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record Purchase Payment</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div><Label>Vendor *</Label><Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v, purchase_id: "" })}><SelectTrigger className="mt-1.5" data-testid="ppay-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger><SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select>{vendorsError && <p className="mt-1 text-xs text-amber-700">{vendorsError}</p>}</div>
            <div><Label>Against Bill (optional)</Label><Select value={form.purchase_id} onValueChange={pickBill} disabled={!form.vendor_id}><SelectTrigger className="mt-1.5" data-testid="ppay-bill-select"><SelectValue placeholder="On-account / select bill" /></SelectTrigger><SelectContent>{!billsError && bills.length === 0 && <div className="px-2 py-1.5 text-sm text-muted-foreground">No pending bills</div>}{bills.map((b) => <SelectItem key={b.id} value={b.id}>{b.doc_no} — bal {inr(b.balance_due)}</SelectItem>)}</SelectContent></Select></div>
            {billsError && <div role="status" className="flex items-center justify-between gap-2 text-xs text-amber-700"><span>{billsError}</span><Button size="sm" variant="outline" onClick={loadBills}>Retry</Button></div>}
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Amount *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: +e.target.value })} className="mt-1.5" data-testid="ppay-amount-input" /></div>
              <div><Label>Mode</Label><Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{["Bank", "Cash", "UPI", "Cheque", "NEFT", "RTGS"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-ppay-button">Save Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
